import { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth, db } from '../config/firebase';
import {
  doc,
  getDoc,
  getDocFromServer,
  getDocFromCache,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

// Removed hard-coded admin emails/UIDs - now only Firestore data determines admin status
// This allows flexyswap@gmail.com to be used as fee account without admin privileges

const isUserAdmin = (data, currentUser) => {
  console.log('🔍 isUserAdmin - Starting check:', {
    hasData: !!data,
    isAdmin: data?.isAdmin,
    isAdminType: typeof data?.isAdmin,
    isAdminStrict: data?.isAdmin === true,
    role: data?.role,
    roleType: typeof data?.role,
    roles: data?.roles,
    rolesType: Array.isArray(data?.roles),
    allKeys: data ? Object.keys(data) : [],
  });

  // Removed hard-coded admin check - now only Firestore data is used
  // This ensures that admin status can be properly managed through the admin panel

  if (!data) {
    console.log('❌ isUserAdmin - No data, returning false');
    return false;
  }

  // Check isAdmin field (handle both boolean true and string "true")
  const isAdminValue = data.isAdmin;
  console.log('🔍 isUserAdmin - Checking isAdmin:', {
    isAdminValue,
    type: typeof isAdminValue,
    '=== true': isAdminValue === true,
    '== true': isAdminValue == true,
    '=== "true"': isAdminValue === 'true',
    '=== 1': isAdminValue === 1,
    '!!value': !!isAdminValue,
  });

  if (
    isAdminValue === true ||
    isAdminValue === 'true' ||
    isAdminValue === 1 ||
    isAdminValue === '1'
  ) {
    console.log('✅ isUserAdmin - isAdmin is truthy, returning true', {
      isAdminValue,
      type: typeof isAdminValue,
    });
    return true;
  }

  // Check role field - more flexible matching
  const roleValue = data.role;
  const primaryRole =
    typeof roleValue === 'string'
      ? roleValue.trim().toLowerCase()
      : String(roleValue || '')
          .trim()
          .toLowerCase();

  console.log('🔍 isUserAdmin - Checking role:', {
    roleValue,
    roleType: typeof roleValue,
    primaryRole,
    isAdminRole: primaryRole === 'admin',
    'roleValue === "admin"': roleValue === 'admin',
    'roleValue === "Admin"': roleValue === 'Admin',
  });

  if (primaryRole === 'admin') {
    console.log('✅ isUserAdmin - role === "admin", returning true');
    return true;
  }

  // Check roles array
  if (Array.isArray(data.roles)) {
    const hasAdminRole = data.roles.some(role => {
      if (typeof role !== 'string') return false;
      return role.trim().toLowerCase() === 'admin';
    });
    console.log('🔍 isUserAdmin - Checking roles array:', {
      roles: data.roles,
      hasAdminRole,
    });
    if (hasAdminRole) {
      console.log(
        '✅ isUserAdmin - Found admin in roles array, returning true',
      );
      return true;
    }
  }

  console.log('❌ isUserAdmin - No admin found, returning false', {
    isAdmin: data.isAdmin,
    role: data.role,
    roles: data.roles,
  });
  return false;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let mounted = true;

    console.log('AuthContext - Setting up auth state listener');

    // Check initial auth state
    const currentAuthUser = auth.currentUser;
    console.log('AuthContext - Initial auth state:', {
      hasUser: !!currentAuthUser,
      userId: currentAuthUser?.uid,
      email: currentAuthUser?.email,
    });

    const unsubscribe = onAuthStateChanged(
      auth,
      async currentUser => {
        console.log('AuthContext - Auth state changed:', {
          hasUser: !!currentUser,
          userId: currentUser?.uid,
          email: currentUser?.email,
        });

        if (!mounted) return;

        try {
          if (currentUser) {
            setUser(currentUser);
            console.log(
              'AuthContext - Setting user, fetching Firestore data...',
            );

            // Fetch user data from Firestore - wait for it before setting loading to false
            try {
              const userDocRef = doc(db, 'users', currentUser.uid);
              console.log(
                '🔍 AuthContext - onAuthStateChanged - Fetching document:',
                {
                  collection: 'users',
                  documentId: currentUser.uid,
                  email: currentUser.email,
                  docPath: `users/${currentUser.uid}`,
                },
              );

              let userDoc = null;
              let data = null;

              // Method 1: Try getDocFromServer
              try {
                userDoc = await getDocFromServer(userDocRef);
                console.log(
                  '🔍 onAuthStateChanged - Method 1 (getDocFromServer) - Document fetch result:',
                  {
                    exists: userDoc.exists(),
                    id: userDoc.id,
                    hasData: userDoc.data() !== undefined,
                  },
                );
                if (userDoc.exists()) {
                  data = userDoc.data();
                }
              } catch (error) {
                console.error(
                  '❌ onAuthStateChanged - Method 1 (getDocFromServer) failed:',
                  error,
                );
              }

              // Method 2: If data is incomplete, try query by email
              if (!data || !data.isAdmin || !data.role) {
                console.log(
                  '⚠️ onAuthStateChanged - Method 1 data incomplete, trying Method 2 (query by email)...',
                );
                try {
                  const emailQuery = query(
                    collection(db, 'users'),
                    where('email', '==', currentUser.email),
                  );
                  const emailSnapshot = await getDocs(emailQuery);
                  if (!emailSnapshot.empty) {
                    const emailDoc = emailSnapshot.docs[0];
                    const emailData = emailDoc.data();
                    console.log(
                      '🔍 onAuthStateChanged - Method 2 (query by email) - Found document:',
                      {
                        id: emailDoc.id,
                        email: emailData.email,
                        isAdmin: emailData.isAdmin,
                        role: emailData.role,
                        allKeys: Object.keys(emailData),
                      },
                    );
                    // Use email query result if it has admin fields
                    if (emailData.isAdmin || emailData.role) {
                      console.log(
                        '✅ onAuthStateChanged - Method 2 worked! Using email query data',
                      );
                      data = emailData;
                      userDoc = {
                        exists: () => true,
                        id: emailDoc.id,
                        data: () => emailData,
                      };
                    }
                  }
                } catch (error) {
                  console.error(
                    '❌ onAuthStateChanged - Method 2 (query by email) failed:',
                    error,
                  );
                }
              }

              // Method 3: Try regular getDoc as final fallback
              if (!data || !data.isAdmin || !data.role) {
                console.log(
                  '⚠️ onAuthStateChanged - Method 2 data incomplete, trying Method 3 (regular getDoc)...',
                );
                try {
                  const fallbackDoc = await getDoc(userDocRef);
                  if (fallbackDoc.exists()) {
                    const fallbackData = fallbackDoc.data();
                    console.log(
                      '🔍 onAuthStateChanged - Method 3 (regular getDoc) - Found document:',
                      {
                        id: fallbackDoc.id,
                        isAdmin: fallbackData.isAdmin,
                        role: fallbackData.role,
                        allKeys: Object.keys(fallbackData),
                      },
                    );
                    if (fallbackData.isAdmin || fallbackData.role) {
                      console.log(
                        '✅ onAuthStateChanged - Method 3 worked! Using fallback data',
                      );
                      data = fallbackData;
                      userDoc = fallbackDoc;
                    }
                  }
                } catch (error) {
                  console.error(
                    '❌ onAuthStateChanged - Method 3 (regular getDoc) failed:',
                    error,
                  );
                }
              }

              if (!mounted) return;

              if (userDoc && userDoc.exists && userDoc.exists()) {
                console.log(
                  '📋 AuthContext - onAuthStateChanged - Raw Firestore data:',
                  {
                    fullData: data,
                    dataKeys: Object.keys(data || {}),
                    dataKeysCount: Object.keys(data || {}).length,
                    role: data?.role,
                    isAdmin: data?.isAdmin,
                    roles: data?.roles,
                    roleType: typeof data?.role,
                    isAdminType: typeof data?.isAdmin,
                    allFields: data,
                  },
                );
                setUserData(data);
                const adminCheck = isUserAdmin(data, currentUser);
                setIsAdmin(adminCheck);
                console.log('AuthContext - User data loaded:', {
                  uid: currentUser.uid,
                  role: data.role,
                  isAdmin: data.isAdmin,
                  roles: data.roles,
                  adminCheckResult: adminCheck,
                  isUserAdminCheck: isUserAdmin(data),
                });
              } else {
                // Kullanıcı Firestore'da yoksa admin değil
                console.log(
                  'AuthContext - User document not found:',
                  currentUser.uid,
                );
                setIsAdmin(false);
                setUserData(null);
              }
            } catch (error) {
              console.error('AuthContext - Error fetching user data:', error);
              if (mounted) {
                setIsAdmin(false);
                setUserData(null);
              }
            } finally {
              if (mounted) {
                setLoading(false);
                console.log('AuthContext - Loading set to false');
              }
            }
          } else {
            // No user
            console.log('AuthContext - No user, clearing state');
            setUser(null);
            setUserData(null);
            setIsAdmin(false);
            if (mounted) {
              setLoading(false);
              console.log('AuthContext - Loading set to false (no user)');
            }
          }
        } catch (error) {
          console.error('AuthContext - Auth state change error:', error);
          if (mounted) {
            setLoading(false);
          }
        }
      },
      error => {
        console.error('AuthContext - Auth state observer error:', error);
        if (mounted) {
          setLoading(false);
        }
      },
    );

    return () => {
      console.log('AuthContext - Cleaning up auth listener');
      mounted = false;
      unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );

      console.log('AuthContext - Login successful, checking admin status:', {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
      });

      // Kullanıcı verilerini kontrol et - try multiple methods
      const userDocRef = doc(db, 'users', userCredential.user.uid);
      console.log('🔍 AuthContext - Fetching document:', {
        collection: 'users',
        documentId: userCredential.user.uid,
        email: userCredential.user.email,
        docPath: `users/${userCredential.user.uid}`,
      });

      let userDoc = null;
      let data = null;

      // Method 1: Try getDocFromServer
      try {
        userDoc = await getDocFromServer(userDocRef);
        console.log('🔍 Method 1 (getDocFromServer) - Document fetch result:', {
          exists: userDoc.exists(),
          id: userDoc.id,
          hasData: userDoc.data() !== undefined,
        });
        if (userDoc.exists()) {
          data = userDoc.data();
        }
      } catch (error) {
        console.error('❌ Method 1 (getDocFromServer) failed:', error);
      }

      // Method 2: If data is incomplete, try query by email
      if (!data || !data.isAdmin || !data.role) {
        console.log(
          '⚠️ Method 1 data incomplete, trying Method 2 (query by email)...',
        );
        try {
          const emailQuery = query(
            collection(db, 'users'),
            where('email', '==', userCredential.user.email),
          );
          const emailSnapshot = await getDocs(emailQuery);
          if (!emailSnapshot.empty) {
            const emailDoc = emailSnapshot.docs[0];
            const emailData = emailDoc.data();
            console.log('🔍 Method 2 (query by email) - Found document:', {
              id: emailDoc.id,
              email: emailData.email,
              isAdmin: emailData.isAdmin,
              role: emailData.role,
              allKeys: Object.keys(emailData),
            });
            // Use email query result if it has admin fields
            if (emailData.isAdmin || emailData.role) {
              console.log('✅ Method 2 worked! Using email query data');
              data = emailData;
              userDoc = {
                exists: () => true,
                id: emailDoc.id,
                data: () => emailData,
              };
            }
          }
        } catch (error) {
          console.error('❌ Method 2 (query by email) failed:', error);
        }
      }

      // Method 3: Try regular getDoc as final fallback
      if (!data || !data.isAdmin || !data.role) {
        console.log(
          '⚠️ Method 2 data incomplete, trying Method 3 (regular getDoc)...',
        );
        try {
          const fallbackDoc = await getDoc(userDocRef);
          if (fallbackDoc.exists()) {
            const fallbackData = fallbackDoc.data();
            console.log('🔍 Method 3 (regular getDoc) - Found document:', {
              id: fallbackDoc.id,
              isAdmin: fallbackData.isAdmin,
              role: fallbackData.role,
              allKeys: Object.keys(fallbackData),
            });
            if (fallbackData.isAdmin || fallbackData.role) {
              console.log('✅ Method 3 worked! Using fallback data');
              data = fallbackData;
              userDoc = fallbackDoc;
            }
          }
        } catch (error) {
          console.error('❌ Method 3 (regular getDoc) failed:', error);
        }
      }

      if (userDoc && userDoc.exists && userDoc.exists()) {
        // CRITICAL DEBUG: Log everything
        console.log(
          '🚨 CRITICAL - Full document data:',
          JSON.stringify(data, null, 2),
        );
        console.log('🚨 CRITICAL - Document ID:', userDoc.id);
        console.log('🚨 CRITICAL - All keys:', Object.keys(data || {}));
        console.log(
          '🚨 CRITICAL - isAdmin value:',
          data?.isAdmin,
          'type:',
          typeof data?.isAdmin,
        );
        console.log(
          '🚨 CRITICAL - role value:',
          data?.role,
          'type:',
          typeof data?.role,
        );

        // If isAdmin/role are missing, try to fetch again with different method
        if (!data.isAdmin && !data.role) {
          console.warn(
            '⚠️ WARNING: isAdmin and role are missing! Trying alternative fetch...',
          );
          // Try regular getDoc as fallback
          const fallbackDoc = await getDoc(userDocRef);
          const fallbackData = fallbackDoc.data();
          console.log(
            '🚨 FALLBACK - Full document data:',
            JSON.stringify(fallbackData, null, 2),
          );
          if (fallbackData?.isAdmin || fallbackData?.role) {
            console.log('✅ FALLBACK worked! Using fallback data');
            Object.assign(data, fallbackData);
          }
        }

        console.log('📋 AuthContext - Login - Raw Firestore data:', {
          fullData: data,
          dataKeys: Object.keys(data || {}),
          dataKeysCount: Object.keys(data || {}).length,
          role: data?.role,
          isAdmin: data?.isAdmin,
          roles: data?.roles,
          roleType: typeof data?.role,
          isAdminType: typeof data?.isAdmin,
          roleValue: data?.role,
          isAdminValue: data?.isAdmin,
          // Show all fields
          allFields: data,
        });
        const adminStatus = isUserAdmin(data, userCredential.user);

        console.log('AuthContext - User document data:', {
          role: data.role,
          isAdmin: data.isAdmin,
          isAdminType: typeof data.isAdmin,
          isAdminStrict: data.isAdmin === true,
          roles: data.roles,
          adminStatus,
          isUserAdminResult: isUserAdmin(data),
          allDataKeys: Object.keys(data),
        });

        // Debug: Log exact values
        console.log('AuthContext - Admin check details:', {
          'data.isAdmin': data.isAdmin,
          'data.isAdmin === true': data.isAdmin === true,
          'data.isAdmin == true': data.isAdmin == true,
          '!!data.isAdmin': !!data.isAdmin,
          'data.role': data.role,
          'data.role === "admin"': data.role === 'admin',
          'data.role?.toLowerCase()': data.role?.toLowerCase(),
        });

        if (!adminStatus) {
          // If not admin, sign out
          console.log('AuthContext - User is not admin, signing out');
          await signOut(auth);
          return {
            success: false,
            error:
              'You do not have permission to access this panel. Only admin users can login.',
          };
        }
      } else {
        // If user doesn't exist in Firestore, not admin
        console.log('AuthContext - User document not found in Firestore');
        await signOut(auth);
        return {
          success: false,
          error: 'User not found or no admin privileges.',
        };
      }

      console.log('AuthContext - Login successful, user is admin');
      return { success: true, user: userCredential.user };
    } catch (error) {
      console.error('AuthContext - Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    userData,
    isAdmin,
    login,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
